import type { Invitation } from "@unbox/api";
import { Mail05 } from "@untitledui/icons";
import { useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { useResendInvite } from "@/api/admin/resend-invite";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CloseButton } from "@/components/base/buttons/close-button";
import { success } from "@/providers/ToastProvider";

type Props = { invite: Pick<Invitation, "id" | "status"> };

export const ResendInviteModal = ({ invite }: Props) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const { mutateAsync, isPending } = useResendInvite();

  const onSubmit = async () => {
    await mutateAsync({ id: invite.id });
    success("Invitation sent");

    close();
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={close}>
      <ButtonUtility
        size="xs"
        color="tertiary"
        tooltip="Resend Invite"
        icon={Mail05}
        onClick={() => setOpen(true)}
        isDisabled={invite.status !== "pending"}
      />
      <ModalOverlay isDismissable>
        <Modal className="dark-mode max-w-md">
          <Dialog>
            <div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-none transition-all sm:w-full">
              <CloseButton onClick={close} size="lg" className="absolute top-3 right-3" />
              <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                <div className="z-10 flex flex-col gap-0.5">
                  <Heading slot="title" className="text-md font-semibold text-primary">
                    Resend invite?
                  </Heading>
                </div>
                <p className="">The user's invitation will be renewed and they will be sent another email</p>
              </div>
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="lg" onClick={close}>
                  Cancel
                </Button>
                <Button color="primary" size="lg" type="button" onClick={onSubmit} isDisabled={isPending}>
                  Resend
                </Button>
              </div>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
